import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Upload, Send, Loader2, Star, StarOff, FileText, Zap, Plus, Search,
  Radio,
} from "lucide-react";
import { toast } from "sonner";
import { fileToCsv, FILE_ACCEPT } from "@/lib/fileToCsv";

// ---------------------------------------------------------------------------
// Which categories support live pulls (RentCast)
const LIVE_KEYS = new Set(["absentee_owner", "high_equity", "senior_owner"]);

// Mapping: our category key → RentCast list_type value expected by /data-lists/search
const LIVE_LIST_TYPE = {
  absentee_owner: "absentee",
  high_equity: "high_equity",
  senior_owner: "senior_owner",
};

function Stars({ count }) {
  return (
    <div className="flex" aria-label={`${count} stars`}>
      {[1, 2, 3, 4, 5].map((i) =>
        i <= count ? (
          <Star key={i} size={12} className="fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" />
        ) : (
          <StarOff key={i} size={12} className="text-muted-foreground/40" />
        ),
      )}
    </div>
  );
}

// ============================================================================
export default function LeadSources() {
  const nav = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addCategory, setAddCategory] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/leads-sources")
      .then((r) => setSources(r.data.sources))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = (src) => { setAddCategory(src); setAddOpen(true); };
  const sendCampaign = (src) => nav(`/marketing?audience=${src.key}&lead_type=${src.key}`);
  const viewList = (src) => nav(`/properties?lead_type=${src.key}`);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="leads-page">
      <div className="reveal">
        <span className="app-eyebrow">/ Distressed & Motivated</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3">Lead Sources</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          The best deals come from sellers with a real reason to sell fast.
          Pull live lists from RentCast or upload your own CSV, then launch a
          targeted campaign.
        </p>
      </div>

      {loading ? (
        <div className="mt-8 text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {sources.map((s, i) => (
            <div
              key={s.key}
              className={`card-flat p-5 reveal reveal-${(i % 4) + 1}`}
              data-testid={`lead-card-${s.key}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    Lead Source
                    {LIVE_KEYS.has(s.key) && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <Radio size={8} className="inline mr-0.5" />LIVE
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-xl font-bold mt-1">{s.label}</h3>
                  <div className="mt-1"><Stars count={s.stars} /></div>
                </div>
                <div className="text-right">
                  <div className="kpi-num text-3xl" data-testid={`lead-count-${s.key}`}>
                    {s.count}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    in list
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 min-h-[36px]">{s.why}</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Button
                  onClick={() => viewList(s)}
                  variant="secondary"
                  className="rounded-sm text-xs h-9"
                  data-testid={`view-${s.key}`}
                >
                  <FileText size={12} className="mr-1" /> View
                </Button>
                <Button
                  onClick={() => openAdd(s)}
                  variant="secondary"
                  className="rounded-sm text-xs h-9"
                  data-testid={`add-${s.key}`}
                >
                  <Plus size={12} className="mr-1" /> Add
                </Button>
                <Button
                  onClick={() => sendCampaign(s)}
                  className="rounded-sm text-xs h-9"
                  data-testid={`campaign-${s.key}`}
                  disabled={s.count === 0}
                >
                  <Send size={12} className="mr-1" /> Blast
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {addCategory && (
        <AddLeadsDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          category={addCategory}
          onImported={load}
        />
      )}
    </div>
  );
}

// ============================================================================
function AddLeadsDialog({ open, onOpenChange, category, onImported }) {
  const canLive = LIVE_KEYS.has(category.key);
  const defaultTab = canLive ? "live" : "import";
  const [tab, setTab] = useState(defaultTab);
  useEffect(() => { setTab(defaultTab); }, [category.key, defaultTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="add-leads-dialog">
        <DialogHeader>
          <DialogTitle>Grow list — {category.label}</DialogTitle>
          <DialogDescription className="text-xs">
            Pull live data or upload a CSV.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-[var(--app-line)]">
          {canLive && (
            <TabBtn active={tab === "live"} onClick={() => setTab("live")}
                    testid="tab-live" icon={<Radio size={12} />} label="Live Pull" />
          )}
          <TabBtn active={tab === "import"} onClick={() => setTab("import")}
                  testid="tab-import" icon={<Upload size={12} />} label="CSV Import" />
        </div>

        <div className="mt-3">
          {tab === "live"   && <LivePanel category={category} onImported={onImported} />}
          {tab === "import" && <ImportPanel category={category} onImported={onImported}
                                            onClose={() => onOpenChange(false)} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, testid, icon, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
        active
          ? "border-[var(--app-black)] text-[var(--app-text)]"
          : "border-transparent text-muted-foreground hover:text-[var(--app-text)]"
      }`}
    >
      <span className="inline-flex items-center gap-1.5">{icon}{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Live RentCast pull
function LivePanel({ category, onImported }) {
  const listType = LIVE_LIST_TYPE[category.key] || "absentee";
  const [city, setCity] = useState("");
  const [state, setState] = useState("FL");
  const [zip, setZip] = useState("");
  const [minBeds, setMinBeds] = useState("");
  const [minYears, setMinYears] = useState(10);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);

  const search = async () => {
    if (!city.trim() || !state.trim()) return toast.error("City and state required");
    setBusy(true); setRows([]); setSelected(new Set());
    try {
      const { data } = await api.post("/data-lists/search", {
        list_type: listType,
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zip_code: zip.trim() || null,
        min_bedrooms: minBeds ? Number(minBeds) : null,
        min_year_owned: Number(minYears) || 10,
        limit: 200,
      });
      setRows(data.rows || []);
      if ((data.rows || []).length === 0) toast.info("No matches — widen filters.");
      else toast.success(`${data.count} matches found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  const toggle = (i) => setSelected((prev) => {
    const s = new Set(prev); s.has(i) ? s.delete(i) : s.add(i); return s;
  });
  const toggleAll = () => setSelected(
    selected.size === rows.length ? new Set() : new Set(rows.map((_, i) => i)),
  );

  const addToCrm = async () => {
    const picks = [...selected].map((i) => rows[i]).filter(Boolean);
    if (!picks.length) return toast.error("Select at least one row");
    setCreating(true);
    try {
      const { data } = await api.post("/data-lists/create-leads", {
        list_type: category.key, rows: picks,
      });
      toast.success(`Added ${data.created} to CRM`);
      setSelected(new Set());
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div><Label className="text-xs">City</Label>
          <Input value={city} onChange={(e) => setCity(e.target.value)}
                 placeholder="Miami" className="mt-1" data-testid="live-city" /></div>
        <div><Label className="text-xs">State</Label>
          <Input value={state} onChange={(e) => setState(e.target.value)}
                 className="mt-1" data-testid="live-state" /></div>
        <div><Label className="text-xs">Zip</Label>
          <Input value={zip} onChange={(e) => setZip(e.target.value)}
                 className="mt-1" data-testid="live-zip" /></div>
        <div><Label className="text-xs">Min Beds</Label>
          <Input type="number" value={minBeds} onChange={(e) => setMinBeds(e.target.value)}
                 className="mt-1" data-testid="live-min-beds" /></div>
        {listType !== "absentee" && (
          <div><Label className="text-xs">Min Yrs Owned</Label>
            <Input type="number" value={minYears} onChange={(e) => setMinYears(e.target.value)}
                   className="mt-1" data-testid="live-min-years" /></div>
        )}
      </div>
      <div className="flex gap-2">
        <Button onClick={search} disabled={busy}
                className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                data-testid="live-search">
          {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Search size={13} className="mr-2" />}
          Search {category.label}
        </Button>
        {rows.length > 0 && (
          <Button onClick={addToCrm} disabled={selected.size === 0 || creating}
                  className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="live-add">
            {creating ? <Loader2 size={13} className="animate-spin mr-2" /> : <Plus size={13} className="mr-2" />}
            Add {selected.size || ""} to CRM
          </Button>
        )}
      </div>
      {rows.length > 0 && (
        <div className="border border-[var(--app-line)] rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-[var(--app-surface)] text-[10px] uppercase tracking-widest">
            <span>{rows.length} results · {selected.size} selected</span>
            <button onClick={toggleAll} className="text-[var(--app-accent)] hover:underline">
              {selected.size === rows.length ? "Deselect all" : "Select all"}
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-2"></th>
                  <th className="text-left p-2">Address</th>
                  <th className="text-left p-2">Owner</th>
                  <th className="text-right p-2">Yrs</th>
                  <th className="text-right p-2">Beds</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}
                      onClick={() => toggle(i)}
                      className={`border-b last:border-b-0 cursor-pointer ${
                        selected.has(i) ? "bg-blue-50" : "hover:bg-[var(--app-surface)]"
                      }`}
                      data-testid={`live-row-${i}`}>
                    <td className="p-2 w-6">
                      <input type="checkbox" checked={selected.has(i)} readOnly />
                    </td>
                    <td className="p-2">{r.address || "—"}</td>
                    <td className="p-2 truncate max-w-[180px]">{r.owner_name || "—"}</td>
                    <td className="p-2 text-right">{r.years_owned ?? "—"}</td>
                    <td className="p-2 text-right">{r.bedrooms ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV / paste import (existing bulk-import flow)
function ImportPanel({ category, onImported, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [detected, setDetected] = useState(0);

  const parseCsv = (t) => {
    const rows = []; let cur = []; let field = ""; let inQ = false;
    for (let i = 0; i < t.length; i++) {
      const c = t[i];
      if (inQ) {
        if (c === '"' && t[i+1] === '"') { field += '"'; i++; continue; }
        if (c === '"') { inQ = false; continue; }
        field += c; continue;
      }
      if (c === '"') { inQ = true; continue; }
      if (c === ",") { cur.push(field); field = ""; continue; }
      if (c === "\r") continue;
      if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; continue; }
      field += c;
    }
    if (field !== "" || cur.length) { cur.push(field); rows.push(cur); }
    return rows.filter((r) => r.some((v) => v && v.trim()));
  };

  const extractAddresses = (t) => {
    if (!t.trim()) return [];
    const rows = parseCsv(t);
    if (rows.length === 0) return [];
    const header = rows[0].map((h) => (h || "").toLowerCase().trim());
    const addressIdx = header.findIndex((h) =>
      ["address", "property_address", "situs_address", "site_address"].includes(h));
    if (addressIdx >= 0) {
      return rows.slice(1).map((r) => (r[addressIdx] || "").trim()).filter(Boolean);
    }
    // fallback: one per line
    return t.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const csv = await fileToCsv(file);
      setText(csv);
      const addrs = extractAddresses(csv);
      setDetected(addrs.length);
      toast.success(`Detected ${addrs.length} address${addrs.length === 1 ? "" : "es"}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't read the file");
    }
    e.target.value = "";
  };

  const onTextChange = (v) => { setText(v); setDetected(extractAddresses(v).length); };

  const submit = async () => {
    const addrs = extractAddresses(text);
    if (!addrs.length) return toast.error("No addresses detected");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-import", {
        lead_type: category.key,
        addresses: addrs,
      });
      toast.success(
        `${data.created} added${data.skipped ? `, ${data.skipped} duplicates skipped` : ""}`,
      );
      setText(""); setDetected(0);
      onImported?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Paste addresses OR upload a CSV / XLSX</Label>
        <label className="inline-flex items-center text-[11px] uppercase tracking-widest border border-[var(--app-line)] rounded-full px-2.5 py-1 cursor-pointer hover:bg-[var(--app-surface)]"
               data-testid="import-file-label">
          <Upload size={11} className="mr-1" /> Upload file
          <input type="file" accept={FILE_ACCEPT} onChange={onFilePick}
                 className="hidden" data-testid="import-file-input" />
        </label>
      </div>
      <Textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        className="field-sharp font-mono text-xs h-44"
        placeholder="Paste addresses (one per line)&#10;123 Main St, Cape Coral, FL 33904&#10;456 Ocean Blvd, Miami, FL 33101"
        data-testid="import-textarea"
      />
      <div className="text-[11px] text-muted-foreground flex items-center justify-between">
        <span data-testid="import-detected-count">
          {detected > 0
            ? `✓ ${detected} address${detected === 1 ? "" : "es"} detected`
            : "0 addresses detected"}
        </span>
        <span><Zap size={10} className="inline mr-1" />No RentCast credits used</span>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy || detected === 0}
                className="rounded-sm" data-testid="submit-import-button">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14} />Importing…</>
                : <><Upload size={14} className="mr-2" />Import addresses</>}
        </Button>
      </DialogFooter>
    </div>
  );
}
